/**
 * <heritage-mobile-nav>
 *
 * Drawer-style mobile navigation. The drawer markup lives inside the
 * custom element. Trigger buttons live elsewhere on the page (typically
 * the header hamburger). Wire them by giving each trigger a selector that
 * matches the value of this element's `data-trigger` attribute (default:
 * `[data-heritage-nav-trigger]`). Close affordances inside the drawer
 * are wired automatically — any element with `data-close`.
 *
 * State is controlled via the `open` boolean attribute, so CSS can target
 * `heritage-mobile-nav[open]` for transitions.
 *
 * Communication: dispatches `heritage:nav:open` and `heritage:nav:close`
 * custom events on itself with `bubbles: true` so other components can
 * react if needed.
 */
class HeritageMobileNav extends HTMLElement {
  connectedCallback() {
    const triggerSelector = this.dataset.trigger || '[data-heritage-nav-trigger]';
    this._triggers = Array.from(document.querySelectorAll(triggerSelector));
    this._closers = Array.from(this.querySelectorAll('[data-close]'));
    this._links = Array.from(this.querySelectorAll('a'));

    this._onTriggerClick = () => this.open();
    this._onCloserClick = () => this.close();
    this._onLinkClick = () => this.close();
    this._onKey = (e) => {
      if (e.key === 'Escape' && this.hasAttribute('open')) this.close();
    };

    this._triggers.forEach((el) => el.addEventListener('click', this._onTriggerClick));
    this._closers.forEach((el) => el.addEventListener('click', this._onCloserClick));
    this._links.forEach((el) => el.addEventListener('click', this._onLinkClick));
    document.addEventListener('keydown', this._onKey);
  }

  disconnectedCallback() {
    this._triggers.forEach((el) => el.removeEventListener('click', this._onTriggerClick));
    this._closers.forEach((el) => el.removeEventListener('click', this._onCloserClick));
    this._links.forEach((el) => el.removeEventListener('click', this._onLinkClick));
    document.removeEventListener('keydown', this._onKey);
    document.body.style.overflow = '';
  }

  open() {
    if (this.hasAttribute('open')) return;
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    this._triggers.forEach((el) => el.setAttribute('aria-expanded', 'true'));
    requestAnimationFrame(() => {
      const focusable = this.querySelector('[data-close], a, button');
      if (focusable) focusable.focus();
    });
    this.dispatchEvent(new CustomEvent('heritage:nav:open', { bubbles: true }));
  }

  close() {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    document.body.style.overflow = '';
    this._triggers.forEach((el) => el.setAttribute('aria-expanded', 'false'));
    this.dispatchEvent(new CustomEvent('heritage:nav:close', { bubbles: true }));
  }
}

if (!customElements.get('heritage-mobile-nav')) {
  customElements.define('heritage-mobile-nav', HeritageMobileNav);
}
